import type { IdTokenResult } from "firebase/auth";

/**
 * The custom claims the Elios backend stamps on an operator's Firebase ID token.
 *
 * Per the backend token contract (`EliosIdentityPlatformStrategy.validate`),
 * admin access requires BOTH:
 *   - `roles` containing `"admin"` (checked by `hasAdminRole`), and
 *   - a non-empty `backendId` (mapped to `AuthenticatedUser.id`).
 *
 * The SPA only gates UX off these; the backend re-enforces them on every write
 * and Firestore rules re-enforce `roles:['admin']` on every read.
 */
export interface OperatorClaims {
  /** Backend User id (`AuthenticatedUser.id`). Empty if the Google account is not provisioned. */
  backendId: string;
  /** Role values carried in the token's `roles` claim. */
  roles: string[];
  /** Convenience: `roles` includes `"admin"`. */
  isAdmin: boolean;
}

export const ADMIN_ROLE = "admin";

export function parseClaims(token: IdTokenResult): OperatorClaims {
  const raw = token.claims as Record<string, unknown>;
  const roles = Array.isArray(raw.roles)
    ? raw.roles.filter((r): r is string => typeof r === "string")
    : [];
  const backendId = typeof raw.backendId === "string" ? raw.backendId : "";
  return {
    backendId,
    roles,
    isAdmin: roles.includes(ADMIN_ROLE),
  };
}

/**
 * A token is accepted by the admin app only when it satisfies the SAME contract
 * the backend enforces: `roles ⊇ ['admin']` AND a non-empty `backendId`.
 */
export function isAdminToken(claims: OperatorClaims): boolean {
  return claims.isAdmin && claims.backendId.length > 0;
}
